import { PropTypes } from 'prop-types';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { connect, useDispatch, useSelector } from 'react-redux';
import AccionaButton from '../../../../../components/AccionaButton';
import AccionaTable from '../../../../../components/AccionaTable';
import AccionaTableButton from '../../../../../components/AccionaTable/AccionaTableButton';
import Modal from '../../../../../components/Modal';
import RemoveModal from '../../../../../components/RemoveModal';
import { MainContext } from '../../../../../context/MainContext';
import WithBackButton from '../../../../../layout/WithBackButton';
import {
  clearCreated,
  clearEdited,
  clearErrors,
  createMonitoring,
  deleteMonitorings,
  getMonitorings,
  updateMonitoring,
  finalizeMonitoring,
} from '../../../../../services/redux/hoodedControl/monitorings/actions';
import CreateMonitoring from '../Create';
import { COLUMNS_HEADERS } from './columns';
import styles from './index.module.scss';
import View from '../View';
import BatteryStatus from '../../../../../components/BatteryStatus';

const { actionsWrapper } = styles;

const List = ({ getItems, deleteItems }) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const { setLoading } = useContext(MainContext);
  const { isCreated, isEdited } = useSelector(
    ({ hoodedControlMonitorings }) => hoodedControlMonitorings,
  );
  const [initialFilters, setInitialFilters] = useState({ sortable: 'id-DESC' });
  const [tableKey, setTableKey] = useState(0);
  const currentFilters = useRef({ sortable: 'id-DESC' });
  const [selectedRows, setSelectedRows] = useState([]);
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [monitoringForEdit, setMonitoringForEdit] = useState();
  const [monitoringForView, setMonitoringForView] = useState();

  const handleTableChange = useCallback(({ filters }) => {
    currentFilters.current = filters;
    if (filters?.sortable?.endsWith('-NONE')) {
      setInitialFilters({ sortable: 'id-DESC' });
    }
  }, []);

  const getItemsWithDefaultSort = useCallback(
    ({ size, page, filters }) => {
      const resolvedFilters = filters?.sortable?.endsWith('-NONE')
        ? { ...filters, sortable: 'id-DESC' }
        : filters;
      getItems({ size, page, filters: resolvedFilters });
    },
    [getItems],
  );

  const onSingleDeleteButtonClick = (monitoring) => {
    setSelectedRows([monitoring]);
    setShowRemoveModal(true);
  };

  const handleOnBulkDeleting = () => {
    setShowRemoveModal(false);
    deleteItems(selectedRows);
    setSelectedRows([]);
  };

  const onCreateMonitoring = (monitoring) => {
    setLoading(true);
    dispatch(createMonitoring(monitoring));
  };

  const editMonitoring = (monitoring) => {
    setLoading(true);
    dispatch(updateMonitoring(monitoring));
  };

  const finishMonitoring = (monitoring) => {
    setLoading(true);
    dispatch(finalizeMonitoring(monitoring));
  };

  useEffect(() => {
    if (isEdited) {
      setLoading(false);
      setMonitoringForEdit(undefined);
      setShowEditModal(false);
      dispatch(clearEdited());
    }
  }, [isEdited, setLoading, dispatch]);

  useEffect(() => {
    if (isCreated) {
      setLoading(false);
      setShowCreateModal(false);
      dispatch(clearCreated());
      setInitialFilters({ ...currentFilters.current });
      setTableKey((prev) => prev + 1);
    }
  }, [isCreated, setLoading, dispatch]);

  useEffect(() => {
    dispatch(clearErrors());
  }, [dispatch, showCreateModal, showEditModal]);

  return (
    <>
      <div className={`row ${actionsWrapper}`}>
        <div className="col-sm-12">
          <AccionaButton
            id="createBtn"
            onClick={() => {
              setShowCreateModal(true);
            }}
            msg={intl.formatMessage({ id: 'button.add' })}
            classIcon="icon fas fa-plus"
            type="white"
          />
        </div>
      </div>
      <div className="row">
        <div className="col-sm-12">
          <AccionaTable
            key={tableKey}
            columns={COLUMNS_HEADERS}
            getItems={getItemsWithDefaultSort}
            entity="hoodedControlMonitorings"
            initialFilters={initialFilters}
            onChange={handleTableChange}
            dataParseFunction={(monitorings) =>
              monitorings.map((monitoring) => ({
                ...monitoring,
                device: (
                  <span
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      whiteSpace: 'pre',
                    }}
                  >
                    {monitoring.device_data?.reference_number}
                    {'  '}
                    {monitoring.device_data?.batt !== undefined ? (
                      <BatteryStatus battery={monitoring.device_data?.batt} />
                    ) : (
                      ''
                    )}
                  </span>
                ),
                finished: monitoring.finished
                  ? intl.formatMessage({ id: 'yes' })
                  : intl.formatMessage({ id: 'no' }),
                rawObject: monitoring,
              }))
            }
            actions={({ rawObject: monitoring }) => (
              <>
                <AccionaTableButton
                  onClick={() => {
                    setShowViewModal(true);
                    setMonitoringForView(monitoring);
                  }}
                  type="view"
                  toolTip={intl.formatMessage({
                    id: 'viewAction.monitorings',
                  })}
                />
                {!monitoring.finished && (
                  <>
                    <AccionaTableButton
                      onClick={() => {
                        setShowEditModal(true);
                        setMonitoringForEdit(monitoring);
                      }}
                      type="edit"
                      toolTip={intl.formatMessage({
                        id: 'editAction.monitorings',
                      })}
                    />
                    <AccionaTableButton
                      onClick={() => {
                        onSingleDeleteButtonClick(monitoring);
                      }}
                      type="delete"
                      toolTip={intl.formatMessage({
                        id: 'deleteAction.monitorings',
                      })}
                    />
                  </>
                )}
              </>
            )}
          />
        </div>
      </div>

      <RemoveModal
        entity="monitorings"
        selectedRows={selectedRows}
        msg={intl.formatMessage(
          { id: 'delete.monitorings' },
          { total: selectedRows.length },
        )}
        onOk={handleOnBulkDeleting}
        onCancel={() => setShowRemoveModal((prev) => !prev)}
        hidden={!showRemoveModal}
      />
      {monitoringForEdit && (
        <Modal
          title={intl
            .formatMessage({
              id: 'strength.hoodedcontrol.monitorings.list.modal.edit.title',
            })
            .toUpperCase()}
          open={showEditModal}
          onCancel={() => {
            setShowEditModal(false);
          }}
          closable
          width="50vw"
        >
          <CreateMonitoring
            initialData={monitoringForEdit}
            onFinish={editMonitoring}
            onFinishComplete={finishMonitoring}
            onCancel={() => {
              setShowEditModal(false);
              setMonitoringForEdit(undefined);
            }}
          />
        </Modal>
      )}
      <Modal
        title={intl
          .formatMessage({
            id: 'strength.hoodedcontrol.monitorings.list.modal.create.title',
          })
          .toUpperCase()}
        open={showCreateModal}
        onCancel={() => {
          setShowCreateModal(false);
        }}
        closable
        width="50vw"
      >
        <CreateMonitoring
          onFinish={onCreateMonitoring}
          onCancel={() => {
            setShowCreateModal(false);
          }}
        />
      </Modal>
      {monitoringForView && (
        <Modal
          title={intl
            .formatMessage({
              id: 'strength.hoodedcontrol.monitorings.list.modal.view.title',
            })
            .toUpperCase()}
          open={showViewModal}
          onCancel={() => {
            setShowViewModal(false);
            setMonitoringForView(undefined);
          }}
          closable
          width="70vw"
        >
          <View
            onCancel={() => {
              setShowViewModal(false);
              setMonitoringForView(undefined);
            }}
            monitoringId={monitoringForView.id}
          />
        </Modal>
      )}
    </>
  );
};

List.propTypes = {
  getItems: PropTypes.func.isRequired,
  deleteItems: PropTypes.func.isRequired,
};

export default connect(() => ({}), {
  getItems: getMonitorings,
  deleteItems: deleteMonitorings,
})(WithBackButton(List));
